import { fromCanonicalKg, toCanonicalKg, type UnitsPreference } from "@jackedlogs/shared";

import { parseLocaleNumber } from "./Logger/parseLocaleNumber";

/**
 * Re-express a weight the athlete can see, from one display unit into the
 * other, going through canonical kg.
 *
 * ⛔ It exists because the alternative — flipping the unit label and leaving
 * the digits — silently multiplies the stored load by 2.2. The cell is very
 * often NOT something the athlete typed: hydrating a past session or copying
 * from history writes `String(fromCanonicalKg(storedKg, unit))` into it, so a
 * label swap turns 220.5 lb into 220.5 kg with nothing on screen to show for
 * it (`MX-6` on the MAX editor, `LG-48` in the logger).
 *
 * ⛔ It converts unconditionally, and that is the decision, not an oversight:
 * provenance ("did YOU type these digits?") is not reliably knowable at this
 * point — `weightSource` answers where the weight came from when it was first
 * logged, which can say "manual" about digits the system has since rewritten.
 * So the tie goes to the failure the athlete can SEE: converting something
 * they typed shows a wrong number they retype, while relabelling something the
 * system seeded looks right and saves wrong.
 *
 * Text that is empty or does not parse comes back untouched — a half-typed
 * cell is not an error to correct, and blanking it would lose keystrokes.
 * Parsing tolerates the comma decimal separator (it/fr/de keyboards); the way
 * back is a bare `String(...)`, the convention every sibling write into a
 * draft cell already follows.
 */
export function convertWeightText(
  text: string,
  from: UnitsPreference,
  to: UnitsPreference,
): string {
  if (from === to) return text;
  const parsed = parseLocaleNumber(text);
  if (!Number.isFinite(parsed)) return text;
  return String(fromCanonicalKg(toCanonicalKg(parsed, from), to));
}
